/**
 * YEREL DEPO — localStorage uygulaması.
 *
 * Worker adresi tanımlı değilken devreye girer. Amaç: uygulama hiçbir
 * sunucu olmadan TAM ÇALIŞSIN. "API yoksa ne olur?" sorusunun cevabı:
 * hiçbir şey bozulmaz, veriler tarayıcıda durur.
 *
 * ⚠️ YEREL MOD GERÇEK KİMLİK DOĞRULAMA DEĞİLDİR.
 * Parola hiçbir yere gönderilmez; oturum "her zaman açık" sayılır. Bu mod
 * TEK KULLANICILI bir çalışma biçimidir, güvenlik sınırı DEĞİLDİR.
 * Gerçek koruma için Worker moduna geçilmelidir.
 */

import type { ContentPack } from '@tttrpg/rules'
import type { AuthState, CharacterRow, InviteSummary, Repository, RollLogEntry } from './contract'

const KEY = {
  characters: 'zarhane:v1:characters',
  packs: 'zarhane:v1:packs',
  rolls: 'zarhane:v1:rolls',
} as const

const LOCAL_USER = {
  id: 'yerel',
  email: 'yerel@zarhane',
  displayName: 'Yerel Oyuncu',
  role: 'owner' as const,
}

/** localStorage erişimi tamamen reddediliyorsa (gizli mod) bellek içi yedek. */
const memory: Record<string, string> = {}

function read<T>(key: string, fallback: T): T {
  let raw: string | null
  try {
    raw = localStorage.getItem(key)
  } catch {
    raw = memory[key] ?? null
  }
  if (raw === null) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    // Bozuk veri: başlangıca dön, uygulamayı düşürme.
    return fallback
  }
}

function write(key: string, value: unknown): void {
  const text = JSON.stringify(value)
  try {
    localStorage.setItem(key, text)
  } catch {
    // Kota dolu / gizli mod: veri bu oturumluk bellekte tutulur.
    memory[key] = text
  }
}

/** Paylaşım belirteci — yerelde de id yerine token kullanılır (tahmin edilemezlik). */
function mintShareToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24))
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const now = () => Date.now()

export function createLocalRepository(): Repository {
  const characters = (): CharacterRow[] => read<CharacterRow[]>(KEY.characters, [])
  const saveCharacters = (rows: CharacterRow[]): void => write(KEY.characters, rows)

  const packs = (): ContentPack[] => read<ContentPack[]>(KEY.packs, [])
  const savePacks = (list: ContentPack[]): void => write(KEY.packs, list)

  const rolls = (): Record<string, RollLogEntry[]> =>
    read<Record<string, RollLogEntry[]>>(KEY.rolls, {})
  const saveRolls = (map: Record<string, RollLogEntry[]>): void => write(KEY.rolls, map)

  const repo: Repository = {
    backend: 'local',

    // --- Kimlik ---------------------------------------------------------
    // Yerel modda oturum her zaman açıktır: parola tutulmaz, doğrulanmaz.
    async getAuth(): Promise<AuthState> {
      return {
        status: 'signed-in',
        userId: LOCAL_USER.id,
        email: LOCAL_USER.email,
        displayName: LOCAL_USER.displayName,
        role: LOCAL_USER.role,
        viaInvite: false,
        backend: 'local',
      }
    },

    async signIn() {
      // Parola sorulmaz; arayüz bu modda giriş ekranı göstermez.
      return { ok: true as const }
    },

    async signUp() {
      return { ok: true as const }
    },

    async signOut() {
      // Anlık görüntü yok; hiçbir şey silinmez.
    },

    async changePassword() {
      // Yerel depoda parola tutulmaz (localStorage'da hesap yok; veri
      // tarayıcıya özeldir). Bu yüzden değiştirilecek bir parola da yok.
      // SESSİZ GEÇMEK YANLIŞ OLUR: kullanıcı "değiştirdim" sanır ama
      // sunucu tarafında hiçbir şey değişmemiş olur.
      return {
        ok: false,
        error: 'Yerel modda parola yoktur. Sunucuya bağlanınca değiştirebilirsin.',
      }
    },

    async setDisplayName() {
      // Aynı gerekçe: yerel depoda hesap kimliği tutulmaz.
      return {
        ok: false,
        error: 'Yerel modda görünen ad yoktur. Sunucuya bağlanınca değiştirebilirsin.',
      }
    },

    // --- Karakterler ----------------------------------------------------
    async listCharacters() {
      return characters().sort((a, b) => a.createdAt - b.createdAt)
    },

    async getCharacter(id) {
      return characters().find((c) => c.id === id) ?? null
    },

    async createCharacter(row) {
      const created: CharacterRow = {
        ...row,
        id: crypto.randomUUID(),
        createdAt: now(),
        updatedAt: now(),
      }
      saveCharacters([...characters(), created])
      return created
    },

    async updateCharacter(id, patch) {
      const list = characters()
      const index = list.findIndex((c) => c.id === id)
      if (index < 0) throw new Error('Karakter bulunamadı.')

      const updated: CharacterRow = { ...list[index]!, ...patch, id, updatedAt: now() }
      const next = [...list]
      next[index] = updated
      saveCharacters(next)
      return updated
    },

    async deleteCharacter(id) {
      saveCharacters(characters().filter((c) => c.id !== id))
    },

    async shareCharacter(id) {
      const token = mintShareToken()
      await repo.updateCharacter(id, { shareToken: token })
      return token
    },

    async getSharedCharacter(shareToken) {
      return characters().find((c) => c.shareToken === shareToken) ?? null
    },

    // --- İçerik paketleri ------------------------------------------------
    async listPacks() {
      return packs()
    },

    async savePack(pack) {
      const list = packs()
      const exists = list.some((p) => p.id === pack.id)
      savePacks(exists ? list.map((p) => (p.id === pack.id ? pack : p)) : [...list, pack])
      return pack
    },

    async deletePack(packId) {
      savePacks(packs().filter((p) => p.id !== packId))
    },

    async getPublicPack(packId) {
      return packs().find((p) => p.id === packId) ?? null
    },

    // --- Zar arşivi -----------------------------------------------------
    async appendRolls(sessionKey, entries) {
      const map = rolls()
      // Sınırlı tutulur: localStorage kotası dolmasın.
      map[sessionKey] = [...(map[sessionKey] ?? []), ...entries]
        .map((entry) => ({ ...entry, createdAt: entry.createdAt ?? now() }))
        .slice(-2000)
      saveRolls(map)
    },

    async listRolls(characterId) {
      const map = rolls()
      return Object.values(map)
        .flat()
        .filter((r) => r.characterId === characterId)
        .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
    },

    // --- Davetler --------------------------------------------------------
    // Yerel modda davet YOKTUR: tek kullanıcı zaten sahibidir. Uygulama bu
    // modda davet ekranını gizler.
    async listInvites(): Promise<InviteSummary[]> {
      return []
    },

    async createInvite(): Promise<InviteSummary> {
      throw new Error('Yerel modda davet oluşturulamaz. Sunucuya geç.')
    },

    async revokeInvite(): Promise<void> {
      throw new Error('Yerel modda davet iptal edilemez. Sunucuya geç.')
    },
  }

  return repo
}
